import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { login, signUp } from '../store/slices/authslice';
import { toast } from 'react-toastify';
import { Mail, Lock, User, Phone, MapPin, Eye, EyeOff } from 'lucide-react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';

const AuthDropdown = ({ isOpen, onClose, initialMode = 'login' }) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    identifier: '',
    username: '',
    password: '',
    phone: '',
    address: '',
    location: ''
  });
  const [mode, setMode] = useState(initialMode);
  const [locationsData, setLocationsData] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [phoneError, setPhoneError] = useState(''); // To hold phone validation error

  useEffect(() => {
    const fetchLocations = async () => {
      const db = getFirestore();
      const locationsCollection = collection(db, 'locations');
      try {
        const snapshot = await getDocs(locationsCollection);
        const locationsList = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLocationsData(locationsList);
      } catch (error) {
        console.error("Error fetching locations: ", error);
        toast.error("Failed to fetch locations.");
      }
    };

    fetchLocations();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      // Validate phone number on change
      const validPhoneRegex = /^(?:\+234|0)[7-9]\d{9}$/;
      if (!validPhoneRegex.test(value)) {
        setPhoneError('Invalid Nigerian phone number. Please ensure it starts with 0 or +234 followed by 10 digits.');
      } else {
        setPhoneError('');
      }
    }

    setFormData({ ...formData, [name]: value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    try {
      // Attempt to log in the user with the provided identifier and password.
      await dispatch(login({
        identifier: formData.identifier,
        password: formData.password
      })).unwrap();
      
      // Show a success message and close the modal on successful login.
      toast.success('You are now logged in!');
      onClose();
    } catch (error) {
      // Log the detailed error for debugging without exposing internal details to the user.
      console.error('Login error:', error);
      
      // Display a generic, user-friendly error message.
      toast.error(
        error.message || 
        "Oops! We couldn't log you in. Please double-check your username and password. If the problem persists, contact support."
      );
    }
  };
  

  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Prevent submission if phone validation fails
    if (phoneError) {
      toast.error(phoneError);
      return;
    }
  
    try {
      // Attempt to sign up the user
      await dispatch(signUp(formData)).unwrap();
      toast.success('Account created successfully!');
      onClose();
    } catch (error) {
      console.error('Sign up error:', error);
  
      /* 
        Instead of displaying any specific error that might hint at the internal workings 
        (like auto-generating emails), we show a generic error message.
        This avoids exposing sensitive implementation details.
      */
      toast.error('Sign up failed. Please verify your details and try again.');
    }
  };
  

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40" onClick={onClose}></div>
      <div className="fixed sm:absolute left-0 sm:left-auto right-0 top-16 sm:top-12 w-full sm:w-[300px] z-50 px-4 sm:px-0">
        <div className="bg-black rounded-lg shadow-lg p-4 mx-auto sm:mx-2 max-w-md sm:max-w-none">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-red-500">
              {mode === 'login' ? 'Login' : 'Create Account'}
            </h2>
            <button onClick={onClose} className="text-red-500 hover:text-red-300">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {mode === 'login' ? (
            <form onSubmit={handleLogin} className="space-y-3">
              <div className="relative">
                <Mail className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  name="identifier"
                  placeholder="Username or Phone"
                  value={formData.identifier}
                  onChange={handleChange}
                  required
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-9 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-red-500"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <button
                type="submit"
                className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm"
              >
                Login
              </button>
            </form>
          ) : (
            <form onSubmit={handleSignup} className="space-y-3">
              <div className="relative">
                <User className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  name="username"
                  placeholder="Username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                />
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full pl-9 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-red-500"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <div className="relative">
                <Phone className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                />
                {phoneError && <p className="text-red-500 text-xs">{phoneError}</p>}
              </div>
              <div className="relative">
                <label htmlFor="location" className="block text-sm font-medium text-gray-300 mb-1">
                  Delivery Location
                </label>
                <select
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  required
                  className="w-full pl-3 pr-10 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                >
                  <option value="">Select Location</option>
                  {locationsData.map(loc => (
                    <option key={loc.id} value={loc.name}>
                      {loc.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 text-gray-400 h-4 w-4" />
                <textarea
                  name="address"
                  placeholder="Delivery Address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  rows="2"
                  className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-black text-white"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors duration-200 text-sm"
              >
                Sign Up
              </button>
            </form>
          )}

          <div className="mt-4 text-center text-sm">
            {mode === 'login' ? (
              <p className="text-gray-400">
                Don't have an account?{' '}
                <button onClick={() => setMode('signup')} className="text-red-600 hover:text-red-700 font-medium">
                  Sign Up
                </button>
              </p>
            ) : (
              <p className="text-gray-400">
                Already have an account?{' '}
                <button onClick={() => setMode('login')} className="text-red-600 hover:text-red-700 font-medium">
                  Login
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AuthDropdown;
